/*
  Estimated delivery date, computed in the browser.

  Replaces the Order Deadline app. The zone table, cutoff and working days come
  from the `delivery_settings` / `delivery_zone` metaobjects via
  snippets/delivery-estimate-config.liquid, which sets window.deliveryEstimate.

  Why the browser and not Liquid: the date depends on the visitor's real
  location, and Liquid can't see it. `localization.country` is the market's
  country, not the buyer's — it reads DE on the EU store for a UK visitor.
  Shopify's own /browsing_context_suggestions.json does return the geolocated
  country, but only over the network. Computing in Liquid would also bake a date
  into a CDN-cached page, which then goes stale the moment the cutoff passes.
*/
(() => {
  const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const GEO_CACHE_KEY = 'delivery-estimate-country';
  const DAY_MS = 86400000;

  /* Dates are held as UTC-midnight timestamps and only ever stepped a whole day
     at a time, so day-of-week arithmetic can't be knocked sideways by DST. */
  const dayKey = (ts) => DAY_KEYS[new Date(ts).getUTCDay()];
  const isoDate = (ts) => new Date(ts).toISOString().slice(0, 10);

  /* The store's own wall clock, which is what the cutoff is set in. */
  function storeNow(timezone) {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
      .formatToParts(new Date())
      .reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {});
    return {
      today: Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)),
      minutes: Number(parts.hour === '24' ? 0 : parts.hour) * 60 + Number(parts.minute),
    };
  }

  function estimate(config, zone) {
    const holidays = new Set(config.holidays || []);
    /* Unset metaobject list fields arrive as null; fall back to Mon-Fri rather
       than treating every day as non-working and running to the step limit. */
    const working = config.workingWeekdays?.length
      ? config.workingWeekdays
      : ['mon', 'tue', 'wed', 'thu', 'fri'];
    /* Carriers don't all deliver on the same days, so a zone may override the
       store default -- e.g. most US destinations get Saturday deliveries but
       Rest of World doesn't. Counting Saturday where the carrier doesn't
       deliver would quote a date earlier than the warehouse can meet. */
    const delivering = zone.deliveryWeekdays?.length
      ? zone.deliveryWeekdays
      : config.deliveryWeekdays?.length
        ? config.deliveryWeekdays
        : working;
    const canWork = (ts) => working.includes(dayKey(ts)) && !holidays.has(isoDate(ts));
    const canDeliver = (ts) => delivering.includes(dayKey(ts)) && !holidays.has(isoDate(ts));
    const step = (ts, ok) => {
      let next = ts;
      /* Bounded so a misconfigured empty weekday list can't spin forever. */
      for (let i = 0; i < 400; i += 1) {
        next += DAY_MS;
        if (ok(next)) return next;
      }
      return next;
    };

    const { today, minutes } = storeNow(config.timezone);
    const [cutoffHour, cutoffMinute] = String(config.cutoffTime).split(':').map(Number);
    const pastCutoff = minutes >= cutoffHour * 60 + (cutoffMinute || 0);

    /* Day 1 of processing is today when today is a working day and the cutoff
       hasn't passed; otherwise it's the next working day. */
    let ship = !canWork(today) || pastCutoff ? step(today, canWork) : today;
    for (let i = 1; i < Math.max(1, config.processingDays); i += 1) ship = step(ship, canWork);

    let cursor = ship;
    for (let i = 0; i < zone.minDays; i += 1) cursor = step(cursor, canDeliver);
    const from = cursor;
    for (let i = zone.minDays; i < zone.maxDays; i += 1) cursor = step(cursor, canDeliver);

    return { ship, from, to: cursor, today };
  }

  /* Boolean metaobject fields don't always reach us as real booleans -- an
     unticked box can arrive as the string "false", which is truthy here and
     would hand every unlisted country the first zone in the list. Only an
     explicit true counts. */
  const isTrue = (value) => value === true || value === 'true';

  function zoneFor(config, countryCode) {
    const zones = config.zones || [];
    return (
      zones.find((z) => (z.countries || []).includes(countryCode)) ||
      zones.find((z) => isTrue(z.fallback)) ||
      null
    );
  }

  /* Countries we deliver to with duties and import taxes already paid, from
     `no_duties_countries` on delivery_settings. "*" means every country, which
     is how the EU and DE stores are set up; UK and US list theirs by code. */
  function noDutiesFor(config, countryCode) {
    const countries = config.noDutiesCountries || [];
    return countries.includes('*') || countries.includes(countryCode);
  }

  /* Shipping price for the visitor's country, from `shipping_prices` on
     delivery_settings ({"PT": {"amount": 8, "currency": "EUR"}, ...}). That
     field is filled from Shopify's shipping settings by a sync script, so the
     line matches checkout. Returns null when the store has no price for it. */
  function shippingFor(config, countryCode) {
    const entry = config.shippingPrices?.[countryCode];
    if (!entry || !entry.currency) return null;
    const amount = Number(entry.amount);
    return Number.isFinite(amount) ? { amount, currency: entry.currency } : null;
  }

  /* `shipping_prices` lists every country the store has a shipping rate for,
     so a country missing from it is one this store doesn't ship to (e.g. a
     German visitor on the UK store). Those get no line at all rather than a
     date checkout can't honour. A store with no prices synced shows the line
     everywhere, as before. */
  function shipsTo(config, countryCode) {
    const prices = config.shippingPrices;
    if (!prices || !Object.keys(prices).length) return true;
    return Object.prototype.hasOwnProperty.call(prices, countryCode);
  }

  /* "€8", "£3", "8 €" in German. A bare "$25" is ambiguous when the UK store
     charges in AUD, CAD or NZD, so any dollar that isn't USD carries its code:
     "$25 CAD". Whole amounts drop the ".00". */
  function formatPrice(shipping, locale) {
    const whole = Number.isInteger(shipping.amount);
    /* US Spanish is the LatAm register, so dollars follow US formatting
       ("$40", not Spain's "40 $"). */
    const formatLocale = shipping.currency === 'USD' && !locale.includes('-') ? `${locale}-US` : locale;
    try {
      const text = new Intl.NumberFormat(formatLocale, {
        style: 'currency',
        currency: shipping.currency,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: whole ? 0 : 2,
      }).format(shipping.amount);
      return shipping.currency !== 'USD' && text.includes('$') ? `${text} ${shipping.currency}` : text;
    } catch (e) {
      return `${shipping.amount} ${shipping.currency}`;
    }
  }

  /* Shopify's native geolocation. Cached for the tab: the answer can't change
     mid-session, and every page has a cart drawer asking for it. */
  let geoPromise = null;
  function geolocate() {
    if (geoPromise) return geoPromise;
    let cached = null;
    try {
      cached = sessionStorage.getItem(GEO_CACHE_KEY);
    } catch (e) {
      /* Private browsing: just re-fetch. */
    }
    if (cached) {
      geoPromise = Promise.resolve(JSON.parse(cached));
      return geoPromise;
    }
    geoPromise = fetch(`${window.Shopify?.routes?.root || '/'}browsing_context_suggestions.json`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        const detected = data?.detected_values?.country;
        if (!detected?.handle) return null;
        const result = { code: detected.handle, name: detected.name };
        try {
          sessionStorage.setItem(GEO_CACHE_KEY, JSON.stringify(result));
        } catch (e) {
          /* Not worth failing over. */
        }
        return result;
      })
      .catch(() => null);
    return geoPromise;
  }

  /* How the day number is written varies by language and can't be derived:
     English wants "22nd", German "22.", French and Italian a bare "20" but an
     irregular first of the month ("1er", "1º"). Both come from the locale file
     via `ordinal_style` (english | dot | plain) and optional `ordinal_first`. */
  function ordinal(day, style, first) {
    if (first && day === 1) return first;
    if (style === 'dot') return `${day}.`;
    if (style === 'plain') return String(day);
    const suffixes = { one: 'st', two: 'nd', few: 'rd', other: 'th' };
    return `${day}${suffixes[new Intl.PluralRules('en', { type: 'ordinal' }).select(day)] || 'th'}`;
  }

  /* `relative` mirrors the app's per-widget setting: the product page prefixed
     "Today"/"Tomorrow" onto the date, the cart message and the recorded
     _Delivers value used the bare date. */
  function formatDate(ts, reference, config, relative = true) {
    const locale = config.locale;
    const strings = decodedStrings(config);
    const days = Math.round((ts - reference) / DAY_MS);
    const prefix = relative && days === 0 ? strings.today : relative && days === 1 ? strings.tomorrow : null;
    const absolute = strings.date_format
      .replace(
        '{{ weekday }}',
        new Intl.DateTimeFormat(locale, { timeZone: 'UTC', weekday: 'long' }).format(ts)
      )
      .replace(
        '{{ day }}',
        ordinal(new Date(ts).getUTCDate(), strings.ordinal_style, strings.ordinal_first)
      )
      .replace(
        '{{ month }}',
        new Intl.DateTimeFormat(locale, { timeZone: 'UTC', month: 'long' }).format(ts)
      );
    /* Matches the app: "Today, Thursday 20th August". */
    return prefix ? `${prefix}, ${absolute}` : absolute;
  }

  function interpolate(template, values) {
    return Object.entries(values).reduce(
      (out, [key, value]) => out.replaceAll(`{{ ${key} }}`, value),
      template
    );
  }

  /* Liquid's `t` filter HTML-escapes anything without an _html suffix, so the
     copy reaches us with `&amp;` for the ampersand in "returns & exchanges" and
     `&#39;` for apostrophes in "Aujourd'hui". These are written to textContent,
     which would show the entities verbatim, so decode them once up front.
     A textarea decodes without parsing tags or running anything. */
  function decodeEntities(value) {
    if (typeof value !== 'string' || !value.includes('&')) return value;
    const decoder = document.createElement('textarea');
    decoder.innerHTML = value;
    return decoder.value;
  }

  function decodedStrings(config) {
    if (!config.decodedStrings) {
      config.decodedStrings = Object.fromEntries(
        Object.entries(config.strings || {}).map(([k, v]) => [k, decodeEntities(v)])
      );
    }
    return config.decodedStrings;
  }

  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      if (this.rendered) return;
      this.rendered = true;
      this.render();
    }

    async render() {
      const config = window.deliveryEstimate;
      if (!config || !config.zones?.length) return;

      const country = await this.resolveCountry(config);
      if (!shipsTo(config, country.code)) return;
      const zone = zoneFor(config, country.code);
      if (!zone) return;

      const dates = estimate(config, zone);
      const strings = decodedStrings(config);
      const messageKey = this.getAttribute('message') || 'in_stock';
      /* The in-stock product line leads with the shipping price when the store
         has one for this country, or free-delivery wording when it's 0. The
         pre-order and cart messages are unchanged. */
      const shipping = messageKey === 'in_stock' ? shippingFor(config, country.code) : null;
      let templateKey = messageKey;
      if (shipping && shipping.amount === 0 && strings.in_stock_free) templateKey = 'in_stock_free';
      else if (shipping && shipping.amount > 0 && strings.in_stock_priced) templateKey = 'in_stock_priced';
      const template = strings[templateKey];
      if (!template) return;

      /* One "delivered by" date by default, taken from the slower end of the
         zone's estimate, so the promise is the one we're confident of. A range
         only appears if the store opts back into it. */
      const formatRange = (relative) => {
        const to = formatDate(dates.to, dates.today, config, relative);
        if (!isTrue(config.showDateRange) || dates.to === dates.from) return to;
        return interpolate(strings.date_range, {
          from: formatDate(dates.from, dates.today, config, relative),
          to,
        });
      };

      let message = interpolate(template, {
        country: country.name,
        date: formatRange(messageKey !== 'cart'),
        price: shipping ? formatPrice(shipping, config.locale) : '',
      });
      /* In-stock product line only, and always last, so the duties promise
         closes the line. The cart message stays a bare date, and the pre-order
         message stands alone as written. */
      if (messageKey === 'in_stock' && strings.no_duties && noDutiesFor(config, country.code)) {
        message = `${message} | ${strings.no_duties}`;
      }
      this.textContent = message;
      this.hidden = false;

      /* Recorded on the order the same way the app did it, via a hidden
         properties[_Delivers] input on the add-to-cart form. Always the bare
         date, so a warehouse pick list never reads "Today". */
      const target = this.getAttribute('records-into');
      if (target) {
        const recorded = formatRange(false);
        document.querySelectorAll(target).forEach((input) => {
          input.value = recorded;
          input.disabled = false;
        });
      }
    }

    /* An explicit country choice always wins: if the selected country differs
       from the store's primary market, the buyer picked it themselves. Only
       when it still sits on the default do we fall back to geolocation. A
       choice this store doesn't ship to (e.g. United Kingdom left in the
       localization cookie on the EU store) is no use for a delivery line, so
       that falls back to geolocation too. */
    async resolveCountry(config) {
      if (
        config.selectedCountry &&
        config.selectedCountry !== config.primaryCountry &&
        shipsTo(config, config.selectedCountry)
      ) {
        return { code: config.selectedCountry, name: config.selectedCountryName };
      }
      const geo = await geolocate();
      if (geo) return geo;
      return { code: config.selectedCountry, name: config.selectedCountryName };
    }
  }

  if (!customElements.get('delivery-estimate')) {
    customElements.define('delivery-estimate', DeliveryEstimate);
  }
})();
